import { useAddChecklistMutation, useGetAllChecklistConfigsQuery } from '@/features/checklist/checklist.api.ts'
import React, { useEffect, useState } from 'react'
import { Button, Flex, Form, Modal, notification, Spin, Tag, Typography } from 'antd'
import { AntdForm } from '@/common/components/AntdForm'
import { AntdFormRules } from '@/common/components/AntdForm/rules.tsx'
import { ChecklistCustomFieldMapping } from '@/features/checklist/checklist.def.ts'
import { handleApiError } from '@/utils/api.tsx'
import { CountryOptions } from '@/common/types/work-info.def.ts'
import { useEmployeePublicInfo } from '@/common/hooks/useEmployeePublicInfo.tsx'
import { useEmployeeDataAccessClaim } from '@/common/hooks/useEmployeeDataAccessClaim.ts'

export type NewChecklistFormValues = {
  checklistConfigId: string
  employeeName: string
  country: string
  employmentEntity: string
  employeeId?: string
  joinDate?: string
  lineManagerEmployeeId?: string
  verbalOfferAcceptanceDate?: string
  noticeDate?: string
  terminationDate?: string
  resignationDate?: string
  lastWorkingDay?: string
}

export const renderChecklistCustomField = (fieldName: string) => {
  const config = ChecklistCustomFieldMapping[fieldName]
  if (!config) return null
  const fieldProps = { label: fieldName, name: config.name, rules: [AntdFormRules.required] }
  switch (config.type) {
    case 'employeeSearch':
      return <AntdForm.EmployeeSearch key={config.name} fieldProps={fieldProps} showActiveOnly />
    case 'date':
      return <AntdForm.DatePicker key={config.name} fieldProps={fieldProps} />
    default:
      return <Typography.Text>Unsupported custom fields detected {fieldName}</Typography.Text>
  }
}

export const NewChecklistForm: React.FC<{ onSuccess?: () => void }> = ({ onSuccess }) => {
  const [createChecklist, { isLoading }] = useAddChecklistMutation()
  const { data: configs, isFetching: configsFetching } = useGetAllChecklistConfigsQuery()
  const [open, setOpen] = useState(false)
  const { getEmployeePublicInfo } = useEmployeePublicInfo()
  const { accessibleEntityOptions } = useEmployeeDataAccessClaim()
  const [form] = Form.useForm<NewChecklistFormValues>()

  const checklistConfigId = Form.useWatch('checklistConfigId', form)
  const employeeId = Form.useWatch('employeeId', form)
  const country = Form.useWatch('country', form)
  const selectedConfig = configs?.find(c => c.id.toString() === checklistConfigId)
  const entityOptions = country ? accessibleEntityOptions.filter(group => group.key === country) : []

  useEffect(() => {
    if (employeeId) {
      const employee = getEmployeePublicInfo(employeeId)
      if (employee) {
        form.setFieldsValue({
          employeeName: employee.displayName,
          lineManagerEmployeeId: employee.reportingManager,
          country: employee.country,
        })
      }
    }
  }, [employeeId, form, getEmployeePublicInfo])

  const onSubmit = async (data: NewChecklistFormValues) => {
    if (!selectedConfig) return
    const result = await createChecklist({
      ...data,
      checklistConfigId: selectedConfig.id,
    })
    if ('data' in result) {
      notification.success({ message: 'Successfully created new checklist' })
      handleCancel()
      onSuccess?.()
    } else if ('error' in result) {
      handleApiError(result.error)
    }
  }

  const handleCancel = () => {
    form.resetFields()
    setOpen(false)
  }

  const checklistConfigOptions = (configs ?? []).map(c => ({ label: c.name, value: c.id.toString() }))

  return (
    <>
      <Button onClick={() => setOpen(true)} type="primary">Add New Checklist</Button>
      <Modal
        open={open}
        title="Add New Checklist"
        destroyOnHidden
        width={850}
        footer={null}
        onCancel={handleCancel}
      >
        <Spin spinning={configsFetching}>
          <Form layout="vertical" scrollToFirstError form={form} onFinish={onSubmit}>
            <AntdForm.Section>
              <AntdForm.Select
                fieldProps={{
                  name: 'checklistConfigId',
                  label: 'Checklist Template',
                  rules: [AntdFormRules.required],
                  span: 16,
                  extra: selectedConfig ? (
                    <Flex className="mt-1">
                      {selectedConfig.category ? <Tag>{selectedConfig.category}</Tag> : null}
                      {selectedConfig.target ? <Tag>{selectedConfig.target}</Tag> : null}
                    </Flex>
                  ) : undefined,
                }}
                options={checklistConfigOptions}
              />
            </AntdForm.Section>
            <AntdForm.Section>
              <AntdForm.Input
                fieldProps={{ name: 'employeeName', label: 'Employee Name', rules: [AntdFormRules.required] }}
              />
              <AntdForm.Select
                fieldProps={{ name: 'country', label: 'Country', rules: [AntdFormRules.required] }}
                options={CountryOptions}
              />
              <AntdForm.Select
                fieldProps={{
                  name: 'employmentEntity',
                  label: 'Employment Entity',
                  rules: [AntdFormRules.required],
                }}
                options={entityOptions}
              />
            </AntdForm.Section>
            {selectedConfig && selectedConfig.customFieldNames.length > 0 && (
              <AntdForm.Section key={selectedConfig.id}>
                {selectedConfig.customFieldNames.map(renderChecklistCustomField)}
              </AntdForm.Section>
            )}
            <Flex gap="small" justify="flex-end">
              <Button htmlType="button" danger onClick={handleCancel}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={isLoading}>Create</Button>
            </Flex>
          </Form>
        </Spin>
      </Modal>
    </>
  )
}
